import { api } from './api';
import {
  ContentListQueryParams,
  ContentListResponse,
  ContentDetailResponse,
  ContentActionResponse,
  BulkActionResponse,
  ContentSummaryResponse,
  TikTokUploadResponse,
} from '../types/content';

const BASE_PATH = '/storage/admin/content';

// ============================================================================
// Content Review API Service
// ============================================================================

/**
 * List content with filters and pagination
 */
export const getContentList = async (
  params: ContentListQueryParams = {}
): Promise<ContentListResponse> => {
  const response = await api.get(BASE_PATH, { params });
  return response.data;
};

/**
 * Get single content item details
 */
export const getContentDetail = async (
  id: string
): Promise<ContentDetailResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}`);
  return response.data;
};

/**
 * Approve a single content item
 */
export const approveContent = async (
  id: string
): Promise<ContentActionResponse> => {
  const response = await api.patch(`${BASE_PATH}/${id}/approve`);
  return response.data;
};

/**
 * Reject a single content item
 */
export const rejectContent = async (
  id: string,
  reason?: string
): Promise<ContentActionResponse> => {
  const response = await api.patch(`${BASE_PATH}/${id}/reject`, { reason });
  return response.data;
};

/**
 * Bulk approve content items (max 50)
 */
export const bulkApproveContent = async (
  ids: string[]
): Promise<BulkActionResponse> => {
  const response = await api.post(`${BASE_PATH}/bulk-approve`, { ids });
  return response.data;
};

/**
 * Bulk reject content items (max 50)
 */
export const bulkRejectContent = async (
  ids: string[],
  reason?: string
): Promise<BulkActionResponse> => {
  const response = await api.post(`${BASE_PATH}/bulk-reject`, { ids, reason });
  return response.data;
};

/**
 * Revert a content item back to pending review: READY → PENDING_REVIEW
 */
export const revertContent = async (
  id: string,
  reason?: string
): Promise<ContentActionResponse> => {
  const response = await api.patch(`${BASE_PATH}/${id}/revert`, { reason });
  return response.data;
};

/**
 * Get content summary/counts by status
 */
export const getContentSummary = async (): Promise<ContentSummaryResponse> => {
  const response = await api.get(`${BASE_PATH}/summary`);
  return response.data;
};

/**
 * Upload a TikTok video file for a pending import item (manual fallback)
 */
export const uploadTikTokVideo = async (
  tiktokVideoId: string,
  file: File
): Promise<TikTokUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(
    `/storage/admin/tiktok/upload/${tiktokVideoId}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

/**
 * Regenerate an AI-generated video — rejects current and queues new generation
 */
export const regenerateContent = async (
  id: string,
  prompt?: string
): Promise<{ success: boolean; rejectedAssetId: string; message: string }> => {
  const response = await api.post(`/admin/content/${id}/regenerate`, prompt ? { prompt } : undefined);
  return response.data;
};

/**
 * Auto-import a TikTok video — backend downloads from TikTok and uploads to blob
 */
export const importTikTokVideo = async (
  tiktokVideoId: string
): Promise<TikTokUploadResponse> => {
  const response = await api.post(
    `/storage/admin/tiktok/import/${tiktokVideoId}`
  );
  return response.data;
};

// ============================================================================
// Export as contentService
// ============================================================================

export const contentService = {
  getContentList,
  getContentDetail,
  approveContent,
  rejectContent,
  revertContent,
  bulkApproveContent,
  bulkRejectContent,
  getContentSummary,
  regenerateContent,
  uploadTikTokVideo,
  importTikTokVideo,
};

export default contentService;
